import type { RefObject } from 'react'
import { useCallback } from 'react'

import { applyMove, applyRemove, computeInsertAt } from '~/components/player/queue-ops'
import type { AudioChannel, EnqueueMode, PlayerState, QueueItem } from '~/components/player/types'
import { BASE_PLAYER_STATE } from '~/components/player/types'
import { useAppStore } from '~/hooks/use-app-store'

interface UseQueueManagementOptions {
  setState: (updater: PlayerState | ((prev: PlayerState) => PlayerState)) => void
  cancelCrossfade: () => void
  channelARef: RefObject<AudioChannel | null>
  channelBRef: RefObject<AudioChannel | null>
  currentTrackIdRef: RefObject<number | null>
}

export function useQueueManagement({
  setState,
  cancelCrossfade,
  channelARef,
  channelBRef,
  currentTrackIdRef,
}: UseQueueManagementOptions) {
  const enqueue = useCallback(
    (tracks: QueueItem[], mode: EnqueueMode = 'after-current') => {
      setState((s) => {
        if (s.queue.length === 0) return { ...s, queue: tracks, currentIndex: 0 }
        const insertAt = computeInsertAt(s.queue, s.currentIndex, mode)
        const queue = [...s.queue.slice(0, insertAt), ...tracks, ...s.queue.slice(insertAt)]
        return { ...s, queue }
      })
    },
    [setState]
  )

  const addToQueue = useCallback(
    (tracks: QueueItem[]) => {
      setState((s) => ({ ...s, queue: [...s.queue, ...tracks] }))
    },
    [setState]
  )

  const insertAt = useCallback(
    (index: number, tracks: QueueItem[]) => {
      setState((s) => {
        const at = Math.max(0, Math.min(index, s.queue.length))
        const queue = [...s.queue.slice(0, at), ...tracks, ...s.queue.slice(at)]
        return { ...s, queue }
      })
    },
    [setState]
  )

  const removeFromQueue = useCallback(
    (index: number) => {
      setState((s) => {
        const { queue, currentIndex } = applyRemove(s.queue, s.currentIndex, index)
        return { ...s, queue, currentIndex }
      })
    },
    [setState]
  )

  const moveInQueue = useCallback(
    (from: number, to: number) => {
      setState((s) => {
        const { queue, currentIndex } = applyMove(s.queue, s.currentIndex, from, to)
        return { ...s, queue, currentIndex }
      })
    },
    [setState]
  )

  const clearQueue = useCallback(() => {
    cancelCrossfade()
    channelARef.current?.audio.pause()
    channelBRef.current?.audio.pause()
    currentTrackIdRef.current = null
    setState({ ...BASE_PLAYER_STATE, ...useAppStore.getState().playerSettings })
  }, [cancelCrossfade, channelARef, channelBRef, currentTrackIdRef, setState])

  const playTrackAt = useCallback(
    (index: number) => {
      cancelCrossfade()
      currentTrackIdRef.current = null
      setState((s) => ({ ...s, currentIndex: index }))
    },
    [cancelCrossfade, currentTrackIdRef, setState]
  )

  return { enqueue, addToQueue, insertAt, removeFromQueue, moveInQueue, clearQueue, playTrackAt }
}
